import { convertirAMonedaLocal } from "./exchangeRateService";

/**
 * Resumen financiero de la inversión de UN edificio, calculado activo por activo sobre los
 * activos ya cargados en el modal Inversión (NO re-fetchea; NO usa getXMensual del ER).
 *
 * Reglas (cerradas en Fase 3A):
 *  - Conversión a moneda local: convertirAMonedaLocal(costo_unitario*cantidad, moneda, pais,
 *    `${añoCompra}-${mesCompra}-15`). TC del día 15 del mes de compra, fijo por activo.
 *    Requiere que las tasas estén cargadas (loadExchangeRates) antes de llamar.
 *  - Meses depreciados (convención canónica, igual que GestionActivos calcularDepreciacion):
 *      mesesTranscurridos = (añoHoy-añoCompra)*12 + (mesHoy-mesCompra), piso 0, techo vida_util.
 *  - Valor residual = 0 (deprecia hasta 0). vida_util = vida_util_meses || 60.
 *  - Segmentación por estado:
 *      EJECUTADO = {activo, en_reparacion}  → inversión real, valor libro, dep. acum/mensual.
 *      PLANIFICADO = {planificado}          → solo "Planificado", separado.
 *      dado_de_baja / vendido               → fuera de todos los totales.
 *  - Enteros: Math.round por activo (no al final).
 *
 * @param {Array} activos  inversionActivos del edificio (cada uno con fecha_compra, costo_unitario,
 *                         cantidad, moneda, vida_util_meses, estado, tipo_activo).
 * @param {string} pais   "Chile" | "Perú".
 * @returns {{
 *   inversionEjecutada: number, inversionPlanificada: number, valorLibroHoy: number,
 *   depreciacionAcumulada: number, depreciacionMensual: number,
 *   porTipo: Array<{tipo_activo: string, inversionEjecutada: number, valorLibroHoy: number}>
 * }}
 */
const EJECUTADO = new Set(["activo", "en_reparacion"]);

const costoLocalActivo = (a, pais) => {
  const costoTotal = (parseFloat(a.costo_unitario) || 0) * (Number(a.cantidad) || 1);
  let fechaDia15 = null;
  if (a.fecha_compra) {
    const [y, m] = a.fecha_compra.split("-");
    fechaDia15 = `${y}-${String(m).padStart(2, "0")}-15`;
  }
  return Math.round(convertirAMonedaLocal(costoTotal, a.moneda, pais, fechaDia15));
};

const mesesTranscurridosActivo = (a, vidaUtil) => {
  if (!a.fecha_compra) return 0;
  const hoy = new Date();
  const [compraY, compraM] = a.fecha_compra.split("-").map(Number);
  const meses = (hoy.getFullYear() - compraY) * 12 + (hoy.getMonth() + 1 - compraM);
  return Math.max(0, Math.min(meses, vidaUtil));
};

export const resumenInversionEdificio = (activos = [], pais) => {
  let inversionEjecutada = 0;
  let inversionPlanificada = 0;
  let valorLibroHoy = 0;
  let depreciacionAcumulada = 0;
  let depreciacionMensual = 0;
  const porTipoMap = new Map(); // tipo_activo -> { inversionEjecutada, valorLibroHoy }

  for (const a of activos) {
    if (a.estado === "planificado") {
      inversionPlanificada += costoLocalActivo(a, pais);
      continue;
    }
    if (!EJECUTADO.has(a.estado)) continue; // dado_de_baja / vendido / otros → fuera

    const vidaUtil = a.vida_util_meses || 60;
    const costoLocal = costoLocalActivo(a, pais);
    const meses = mesesTranscurridosActivo(a, vidaUtil);
    const cuotaLocal = costoLocal / vidaUtil;
    const depAcum = Math.round(cuotaLocal * meses);
    const nbv = Math.max(0, costoLocal - depAcum);
    // Sigue depreciando solo si no agotó la vida útil.
    const cuotaMensual = meses < vidaUtil ? Math.round(cuotaLocal) : 0;

    inversionEjecutada += costoLocal;
    valorLibroHoy += nbv;
    depreciacionAcumulada += costoLocal - nbv; // = Σ dep. acumulada, coherente con costo - NBV
    depreciacionMensual += cuotaMensual;

    const tipo = a.tipo_activo || "otro";
    const acc = porTipoMap.get(tipo) || { inversionEjecutada: 0, valorLibroHoy: 0 };
    acc.inversionEjecutada += costoLocal;
    acc.valorLibroHoy += nbv;
    porTipoMap.set(tipo, acc);
  }

  const porTipo = [...porTipoMap.entries()]
    .map(([tipo_activo, v]) => ({ tipo_activo, ...v }))
    .sort((x, y) => y.inversionEjecutada - x.inversionEjecutada);

  return {
    inversionEjecutada,
    inversionPlanificada,
    valorLibroHoy,
    depreciacionAcumulada,
    depreciacionMensual,
    porTipo,
  };
};
